import { useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Printer, Download, Mail } from 'lucide-react';
import { useApp } from '@/store';
import { useT, type I18nKey } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { StatusBadge } from '@/components/StatusBadge';
import { exportPdf } from '@/lib/pdf';
import { ResponsiveGrid } from '@/components/layout';

const conditionKeyMap: Record<string, I18nKey> = {
  fireCock: 'hoseReport.conditionFireCock',
  seals: 'hoseReport.conditionSeals',
  hose: 'hoseReport.conditionHose',
  nozzle: 'hoseReport.conditionNozzle',
  axle: 'hoseReport.conditionAxle',
  swivelArm: 'hoseReport.conditionSwivelArm',
  doorLock: 'hoseReport.conditionDoorLock',
  water: 'hoseReport.conditionWater',
};

export function HoseReport() {
  const t = useT();
  const { id } = useParams();
  const customer = useApp(s => s.customers.find(c => c.id === id));
  const settings = useApp(s => s.settings);
  const printRef = useRef<HTMLDivElement>(null);

  if (!customer) return <div>{t('common.notFound')}</div>;

  const handlePdf = () => {
    if (printRef.current) {
      exportPdf(printRef.current, `Hose-Report-${customer.id}.pdf`);
    }
  };

  return (
    <div className="space-y-4">
      <div className="no-print flex justify-between items-center">
        <Link
          to={`/customer/${customer.id}/hoses`}
          className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1"
        >
          <ArrowLeft className="h-3 w-3" /> {t('common.back')}
        </Link>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => window.print()}>
            <Printer className="h-4 w-4" />
            {t('common.print')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            data-focus="hose-report-email"
            onClick={() => {
              const email =
                customer.billingAddress?.email ?? customer.contact?.email ?? '';
              const subject = encodeURIComponent(
                `Hose Service Report — ${customer.name}`
              );
              const body = encodeURIComponent(
                `${settings.companyName}\nHose Service Report: ${customer.name}\n${customer.hoses.length} hose(s)`
              );
              window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
            }}
          >
            <Mail className="h-4 w-4" />
            {t('common.email')}
          </Button>
          <Button size="sm" onClick={handlePdf}>
            <Download className="h-4 w-4" />
            {t('common.pdf')}
          </Button>
        </div>
      </div>

      <Card
        ref={printRef}
        className="print-page max-w-[210mm] mx-auto p-4 sm:p-8"
      >
        <div className="border-b pb-4 mb-6 grid gap-4 sm:grid-cols-2">
          <div>
            <div className="font-bold text-lg">{settings.companyName}</div>
            <div className="text-xs">{settings.street}</div>
            <div className="text-xs">
              {settings.zip} {settings.city}
            </div>
            <div className="text-xs">
              {settings.phone} · {settings.email}
            </div>
          </div>
          <div className="text-right">
            <div className="text-lg font-bold text-primary">
              {t('hoseReport.title')}
            </div>
            <div className="text-xs mt-1">
              {t('hoseReport.date')}: {new Date().toISOString().slice(0, 10)}
            </div>
            <div className="text-xs">
              {t('hoseReport.customer')}:{' '}
              <span className="font-mono">{customer.id}</span>
            </div>
          </div>
        </div>

        <ResponsiveGrid cols={2} className="text-sm mb-6">
          <div>
            <div className="font-semibold mb-1">
              {t('customer.billingAddress')}
            </div>
            <div className="text-xs">
              {customer.billingAddress?.company}
              <br />
              {customer.billingAddress?.street}
              <br />
              {customer.billingAddress?.zip} {customer.billingAddress?.city}
            </div>
          </div>
          <div>
            <div className="font-semibold mb-1">
              {t('hoseReport.objectSite')}
            </div>
            <div className="text-xs">
              {customer.objectAddress?.company}
              <br />
              {customer.objectAddress?.street}
              <br />
              {customer.objectAddress?.zip} {customer.objectAddress?.city}
            </div>
          </div>
        </ResponsiveGrid>

        <div className="rounded bg-blue-50 dark:bg-blue-950/30 p-3 text-xs mb-6">
          {t('hoseReport.standard')} {customer.hoses.length}
        </div>

        <div className="space-y-4">
          {customer.hoses.map((h, i) => (
            <div key={h.id} className="border rounded p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="font-semibold text-sm">
                    #{i + 1} — {h.mfr} {h.model}{' '}
                    <span className="font-mono text-xs text-muted-foreground">
                      {h.id}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {h.location}
                  </div>
                </div>
                <StatusBadge status={h.status} />
              </div>
              <ResponsiveGrid cols={3} className="text-xs">
                <div>
                  <div className="font-semibold mb-1">
                    {t('hoseReport.equipment')}
                  </div>
                  <div>
                    {t('hoseReport.year')}: {h.year}
                  </div>
                  <div>
                    {t('hoseReport.length')}: {h.length} m · Ø {h.diameter}
                  </div>
                  <div>
                    {t('hoseReport.connection')}: {h.connection}
                  </div>
                </div>
                <div>
                  <div className="font-semibold mb-1">
                    {t('hoseReport.condition')}
                  </div>
                  {Object.entries(h.condition).map(([k, v]) => (
                    <div key={k} className="flex justify-between">
                      <span>
                        {conditionKeyMap[k] ? t(conditionKeyMap[k]) : k}
                      </span>
                      <span>{v}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="font-semibold mb-1">
                    {t('hoseReport.measurements')}
                  </div>
                  <div>
                    {t('hoseReport.resting')}: {h.restingPressure}
                  </div>
                  <div>
                    {t('hoseReport.flow')}: {h.flowPressure}
                  </div>
                  <div>
                    {t('hoseReport.flowRate')}: {h.flowRate}
                  </div>
                  <div>
                    {t('hoseReport.pressureTest')}: {h.pressureTestDate}
                  </div>
                </div>
              </ResponsiveGrid>
            </div>
          ))}
          {customer.hoses.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8">
              {t('hoseReport.noHoses')}
            </p>
          )}
        </div>

        <div className="grid gap-8 sm:grid-cols-2 mt-12 pt-8 border-t text-xs">
          <div>
            <div className="border-b border-foreground pb-1 mb-1">&nbsp;</div>
            <div>{t('hoseReport.techSignature')}</div>
          </div>
          <div>
            <div className="border-b border-foreground pb-1 mb-1">&nbsp;</div>
            <div>{t('hoseReport.custSignature')}</div>
          </div>
        </div>
      </Card>
    </div>
  );
}
